<template>
  <el-card class="table-card section-card" aria-live="polite" shadow="never">
    <el-table
      :data="items"
      v-loading="loading"
      stripe
      class="requests-table"
      header-cell-class-name="requests-table__header"
      empty-text="No cases to show."
      height="460"
    >
      <el-table-column prop="id" label="Case ID" min-width="170" align="center">
        <template #default="scope">
          <span class="mono">{{ scope.row.id }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Submission" min-width="220">
        <template #default="scope">
          <div class="submission-cell">
            <strong>{{ scope.row.submissionName || scope.row.submission?.submissionName || 'Untitled case' }}</strong>
            <span>{{ scope.row.submissionType || scope.row.submission?.submissionType || 'No submission type' }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Status" min-width="120" align="center">
        <template #default="scope">
          <el-tag :type="statusType(scope.row.status)" round effect="light">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Assigned To" min-width="190" align="center">
        <template #default="scope">
          <span class="assigned-label">
            {{ scope.row.assignedToName || scope.row.assignedToEmail || 'Pending assignment' }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="Updated" min-width="190" align="center">
        <template #default="scope">
          <span class="update-date">{{ formatDate(scope.row.updatedAt) }}</span>
        </template>
      </el-table-column>

      <el-table-column label="Info" align="center" min-width="120">
        <template #default="scope">
          <el-button type="primary" text @click="openDetails(scope.row)">
            <el-icon><InfoFilled /></el-icon>&nbsp;
            Details
          </el-button>
        </template>
      </el-table-column>

      <template #empty>
        <el-empty description="No cases to show." />
      </template>
    </el-table>

    <CaseDetailsDialog
      v-if="showDetails"
      :caseId="selectedCaseId"
      :initial-case="selectedCase"
      :visible="showDetails"
      @close="showDetails = false; selectedCaseId = null; selectedCase = null"
    />
  </el-card>
</template>

<script>
import CaseDetailsDialog from '@/components/CaseDetailsDialog.vue'
import { CASE_SCOPES, watchList } from '@/services/requests'

export default {
  name: 'RequestsList',
  components: { CaseDetailsDialog },
  props: {
    mode: {
      type: String,
      required: true,
    },
    currentUser: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      items: [],
      loading: true,
      unsub: null,
      showDetails: false,
      selectedCaseId: null,
      selectedCase: null,
    }
  },
  computed: {
    scope() {
      return this.mode === 'past' ? CASE_SCOPES.CUSTOMER_PAST : CASE_SCOPES.CUSTOMER_ACTIVE
    },
  },
  watch: {
    mode: 'subscribe',
    currentUser: 'subscribe',
  },
  mounted() {
    this.subscribe()
  },
  beforeUnmount() {
    this.unsub && this.unsub()
  },
  methods: {
    subscribe() {
      this.unsub && this.unsub()
      const uid = this.currentUser?.uid

      if (!uid) {
        this.items = []
        this.loading = false
        return
      }

      this.loading = true
      this.unsub = watchList({ scope: this.scope, uid }, (rows) => {
        this.items = rows
        this.loading = false
        this.$emit('countsChanged', {
          [this.mode === 'past' ? 'past' : 'active']: rows.length,
        })
      })
    },
    statusType(status) {
      if (!status) return 'info'
      const normalized = String(status).toLowerCase().trim()
      if (normalized === 'submitted') return 'primary'
      if (normalized === 'in-progress' || normalized === 'in progress') return 'warning'
      if (normalized === 'in-review' || normalized === 'in review') return 'info'
      if (normalized.includes('review') && normalized.includes('done')) return 'success'
      if (normalized === 'completed' || normalized === 'closed') return 'success'
      if (normalized === 'rejected' || normalized === 'denied') return 'danger'
      return 'info'
    },
    formatDate(ts) {
      if (!ts) return ''
      try {
        const ms = ts.toMillis ? ts.toMillis() : (ts.seconds || 0) * 1000
        return new Date(ms).toLocaleString()
      } catch {
        return ''
      }
    },
    openDetails(row) {
      this.selectedCase = row
      this.selectedCaseId = row?.id || null
      this.showDetails = true
    },
  },
}
</script>

<style scoped>
.table-card {
  padding: 12px;
  border-radius: 22px;
}

.requests-table__header {
  background: #003831;
  color: #ffffff;
  font-weight: 800;
  text-align: center;
}

:deep(.el-table__header th.requests-table__header) {
  background: #003831 !important;
  color: #ffffff !important;
  text-align: center;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 15px;
  color: #111827;
}

.submission-cell {
  display: grid;
  gap: 4px;
}

.submission-cell strong {
  color: #03241f;
}

.submission-cell span,
.assigned-label,
.update-date {
  color: #4f6d67;
  font-size: 14px;
}

@media (max-width: 720px) {
  .table-card {
    padding: 8px;
  }
}
</style>
